import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../components/feedback/Modal';
import { Button } from '../../components/actions/Button';
import { FormField } from '../../components/forms/FormField';
import { TextField } from '../../components/forms/TextField';
import { isApiError } from '../../lib/api/errors';
import { formatPhoneInput, toE164, isValidPhone } from '../../utils/phoneNumber';
import { useConfirmPhoneChange, useRequestPhoneChangeCode, useUpdateEmail } from './queries';
import { MIN_PASSWORD_LENGTH } from '../mfa/api';
import { useSetAccountPassword } from '../mfa/queries';
import { notify } from '../../services/notify';

/**
 * Email and phone changes, ported from core's EditEmailModal / EditPhoneModal.
 * Email is not OTP-verified (parity with core), so the dialog asks for a
 * double-check before saving. Phone is two steps: SMS a code, then confirm.
 *
 * The caller mounts each dialog only while it is open, so every open starts
 * from fresh state — no reset effects.
 */

const emailSchema = z.object({ email: z.string().trim().email('Enter a valid email address.') });

export function EmailDialog({
  onClose,
  profileId,
  currentEmail,
}: {
  onClose: () => void;
  profileId: string;
  currentEmail: string;
}) {
  const update = useUpdateEmail(profileId);
  const [confirming, setConfirming] = useState(false);
  const form = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: currentEmail },
  });

  const submit = form.handleSubmit(async ({ email }) => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    try {
      await update.mutateAsync({ email });
      onClose();
    } catch (err) {
      setConfirming(false);
      form.setError('email', {
        message:
          isApiError(err) && err.status === 409
            ? 'That email is already in use on another account.'
            : 'The email could not be updated. Try again.',
      });
    }
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={confirming ? 'Double-check your email' : 'Update email'}
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={confirming ? () => setConfirming(false) : onClose}>
            {confirming ? 'Back' : 'Cancel'}
          </Button>
          <Button size="sm" type="submit" form="email-form" loading={update.isPending}>
            {confirming ? 'Save this email' : 'Continue'}
          </Button>
        </>
      }
    >
      <form id="email-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        {confirming ? (
          <p className="m-0 text-sm text-muted">
            Email changes are not verified. Check that <strong className="text-ink">{form.getValues('email')}</strong>{' '}
            is spelled correctly — a typo makes sign-in and alert emails harder to receive.
          </p>
        ) : (
          <FormField label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
            <TextField
              id="email"
              type="email"
              autoComplete="email"
              invalid={!!form.formState.errors.email}
              {...form.register('email')}
            />
          </FormField>
        )}
      </form>
    </Modal>
  );
}

/**
 * Update phone: send a code to the new number, then confirm it. `required` is
 * the admin MFA case (no phone on the profile yet): a blocking dialog — no
 * Cancel, no close — titled for MFA; `onClose` then means "done".
 */
export function PhoneDialog({
  onClose,
  currentPhone,
  required = false,
}: {
  onClose: () => void;
  currentPhone?: string;
  required?: boolean;
}) {
  const requestCode = useRequestPhoneChangeCode();
  const confirm = useConfirmPhoneChange();
  const [step, setStep] = useState<'send' | 'verify'>('send');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const e164 = toE164(phone);
  const sameAsCurrent = !!e164 && e164 === currentPhone;

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!e164 || !isValidPhone(phone) || sameAsCurrent) return;
    setError('');
    try {
      await requestCode.mutateAsync({ phone: e164 });
      setStep('verify');
    } catch (err) {
      setError(
        isApiError(err) && err.status === 409
          ? 'That phone number is already in use on another account.'
          : 'The code could not be sent. Try again.',
      );
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6) return;
    setError('');
    try {
      await confirm.mutateAsync({ code });
      onClose();
    } catch (err) {
      if (isApiError(err) && err.status === 410) {
        setError('That code has expired. Request a new one.');
        setCode('');
        setStep('send');
      } else {
        setError('That code is not valid. Try again.');
      }
    }
  };

  const isSend = step === 'send';
  return (
    <Modal
      isOpen
      onClose={required ? () => undefined : onClose}
      hideCloseButton={required}
      size="md"
      title={isSend ? (required ? 'Phone number required for MFA' : 'Update phone number') : 'Enter the code'}
      footer={
        <>
          {!required && (
            <Button variant="ghost-dark" size="sm" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button
            size="sm"
            type="submit"
            form="phone-form"
            loading={isSend ? requestCode.isPending : confirm.isPending}
            disabled={isSend ? !isValidPhone(phone) || sameAsCurrent : code.length < 6}
          >
            {isSend ? 'Send code' : 'Confirm'}
          </Button>
        </>
      }
    >
      <form id="phone-form" onSubmit={isSend ? send : verify} noValidate className="flex flex-col gap-4">
        <p className="m-0 text-sm text-muted">
          {isSend
            ? required
              ? 'Admin access needs a phone number for sign-in codes on new devices. We text a 6-digit code to confirm it can receive messages.'
              : 'We text a 6-digit code to confirm the new number can receive messages.'
            : 'Enter the 6-digit code from the text message.'}
        </p>
        {isSend ? (
          <FormField
            label="Phone"
            htmlFor="phone"
            error={error || (sameAsCurrent ? 'This is already your phone number.' : undefined)}
          >
            <TextField
              id="phone"
              type="tel"
              autoComplete="tel"
              placeholder="+1 (303) 555-1234"
              value={phone}
              invalid={!!error || sameAsCurrent}
              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
            />
          </FormField>
        ) : (
          <FormField label="Code" htmlFor="code" error={error || undefined}>
            <TextField
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              invalid={!!error}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </FormField>
        )}
      </form>
    </Modal>
  );
}

/** Set the signed-in admin's password: new + confirm. The BE needs a verified device (admin MFA). */
export function PasswordDialog({ onClose }: { onClose: () => void }) {
  const save = useSetAccountPassword();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirmation.length > 0 && confirmation !== password;
  const ready = password.length >= MIN_PASSWORD_LENGTH && confirmation === password;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setError('');
    try {
      await save.mutateAsync(password);
      notify.success('Password set.');
      onClose();
    } catch {
      setError('The password could not be saved. Try again.');
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title="Set password"
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" type="submit" form="password-form" loading={save.isPending} disabled={!ready}>
            Save
          </Button>
        </>
      }
    >
      <form id="password-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        <FormField
          label="New password"
          htmlFor="new-password"
          hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          error={tooShort ? `Use at least ${MIN_PASSWORD_LENGTH} characters.` : error || undefined}
        >
          <TextField
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            invalid={tooShort || !!error}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormField>
        <FormField
          label="Confirm password"
          htmlFor="confirm-password"
          error={mismatch ? 'The passwords do not match.' : undefined}
        >
          <TextField
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            invalid={mismatch}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </FormField>
      </form>
    </Modal>
  );
}
